import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Label,
  Button,
} from "reactstrap";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

import apiClient from "../../axiosInstance";
import TanstackTable from "../../components/Common/TanstackTable";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import logo from "../../assets/images/logo.png";

const RegistrationsList = () => {
  const [breadcrumbItems] = useState([
    { title: "Daryeel", link: "/" },
    { title: "Vehicle Registrations", link: "#" },
  ]);

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({ from_date: "", to_date: "" });

  const fetchRegistrations = async () => {
    try {
      const result = await apiClient.get("/registrations/vehicle-registrations");
      setData(result.data || []);
    } catch (err) {
      console.error("Error fetching registrations:", err);
    }
  };

  useEffect(() => {
    fetchRegistrations();
  }, []);

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const applyFilters = async () => {
    if (
      filters.from_date &&
      filters.to_date &&
      filters.from_date > filters.to_date
    ) {
      await Swal.fire({
        title: "Invalid Date Range",
        text: "From date cannot be greater than To date",
        icon: "warning",
      });
      return;
    }

    const fromDate = filters.from_date
      ? new Date(`${filters.from_date}T00:00:00`)
      : null;
    const toDate = filters.to_date
      ? new Date(`${filters.to_date}T23:59:59`)
      : null;

    const nextData = data.filter((row) => {
      const rowDateValue = row.registration_date;
      if (!rowDateValue) {
        return !fromDate && !toDate;
      }

      const rowDate = new Date(rowDateValue);
      if (Number.isNaN(rowDate.getTime())) {
        return !fromDate && !toDate;
      }

      if (fromDate && rowDate < fromDate) {
        return false;
      }
      if (toDate && rowDate > toDate) {
        return false;
      }
      return true;
    });

    setFilteredData(nextData);
  };

  const resetFilters = () => {
    setFilters({ from_date: "", to_date: "" });
    setFilteredData(data);
  };

  const totalRecords = data.length;
  const visibleRecords = filteredData.length;

  const handlePrint = async () => {
    if (!filteredData.length) {
      await Swal.fire({
        title: "Vehicle Registrations",
        text: "No data available to print",
        icon: "warning",
      });
      return;
    }

    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const formatDate = (value) => {
      if (!value) {
        return "";
      }
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime())
        ? ""
        : parsed.toLocaleDateString();
    };

    const rowsHtml = filteredData
      .map(
        (row, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${escapeHtml(`${row.make_name || ""} ${row.model_name || ""}`.trim())}</td>
            <td>${escapeHtml(row.license_plate || "")}</td>
            <td>${escapeHtml(row.full_name || "")}</td>
            <td>${escapeHtml(row.branch_name || "")}</td>
            <td>${escapeHtml(formatDate(row.registration_date))}</td>
            <td>${escapeHtml(formatDate(row.expiry_date))}</td>
            <td>${escapeHtml(row.status || "")}</td>
          </tr>
        `
      )
      .join("");

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      await Swal.fire({
        title: "Vehicle Registrations",
        text: "Unable to open print window. Please allow popups and try again.",
        icon: "error",
      });
      return;
    }

    const generatedAt = new Date().toLocaleString();

    printWindow.document.write(`
      <html>
        <head>
          <title>Vehicle Registrations</title>
          <style>
            @page { size: A4 landscape; margin: 15mm 14mm; }
            body { font-family: "Segoe UI", Arial, sans-serif; margin: 18px; color: #1f2937; }
            .header { text-align: center; border-bottom: 2px solid #6b7280; padding-bottom: 12px; margin-bottom: 12px; }
            .logo { width: 70mm; height: 50mm; object-fit: contain; padding-top: 10mm; margin-bottom: 6px; margin-top: -50px; }
            h2 { margin: 0 0 6px; font-weight: 700; }
            .meta { margin-bottom: 14px; font-size: 12px; color: #374151; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px 18px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #6b7280; padding: 7px 8px; text-align: left; }
            th { background: #e5e7eb; color: #111827; }
            tbody tr:nth-child(even) { background: #f9fafb; }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="${logo}" alt="Daryeel Logo" class="logo" />
            <h2>Vehicle Registrations Report</h2>
          </div>
          <div class="meta">
            <div><strong>From Date:</strong> ${escapeHtml(filters.from_date || "-")}</div>
            <div><strong>To Date:</strong> ${escapeHtml(filters.to_date || "-")}</div>
            <div><strong>Total Records:</strong> ${filteredData.length}</div>
            <div><strong>Generated:</strong> ${escapeHtml(generatedAt)}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>No</th>
                <th>Vehicle</th>
                <th>License Plate</th>
                <th>Owner</th>
                <th>Branch</th>
                <th>Reg. Date</th>
                <th>Expiry Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleDeleteRegistration = async (registrationId) => {
    const confirmResult = await Swal.fire({
      title: "Delete Registration?",
      text: "Are you sure you want to delete this vehicle registration?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    try {
      const response = await apiClient.delete(
        `/registrations/vehicle-registrations/${registrationId}`
      );

      if (response.data.status === "success") {
        setData((prevData) =>
          prevData.filter((registration) => registration.id !== registrationId)
        );
        await Swal.fire({
          title: "Deleted",
          text:
            response.data.message || "Vehicle registration deleted successfully",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        return;
      }

      await Swal.fire({
        title: "Delete Failed",
        text: response.data.message || "Failed to delete vehicle registration",
        icon: "error",
      });
    } catch (error) {
      console.error("Error deleting vehicle registration:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to delete vehicle registration";
      await Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
      });
    }
  };
  const columns = [
    {
      id: "index",
      header: "No",
      cell: ({ row }) => row.index + 1,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      accessorFn: (row) =>
        `${row.make_name || ""} ${row.model_name || ""} ${
          row.license_plate || ""
        }`,
      id: "vehicle",
      header: "Vehicle",
      cell: ({ row }) => {
        const { make_name, model_name, license_plate } = row.original;
        return (
          <>
            <strong>
              {make_name || ""} {model_name || ""}
            </strong>
            {license_plate && (
              <span
                className="badge bg-info ms-2"
                style={{ fontSize: "0.75em", verticalAlign: "middle" }}
              >
                {license_plate}
              </span>
            )}
          </>
        );
      },
    },
    {
      accessorFn: (row) =>
        `${row.full_name || ""} ${row.phone_number || ""}`,
      id: "owner",
      header: "Owner",
      cell: ({ row }) => {
        const { full_name, phone_number } = row.original;
        return (
          <>
            {full_name || ""}
            {phone_number && (
              <span
                className="badge bg-secondary ms-2"
                style={{ fontSize: "0.75em", verticalAlign: "middle" }}
              >
                {phone_number}
              </span>
            )}
          </>
        );
      },
    },
    {
      accessorKey: "branch_name",
      header: "Branch",
    },
    {
      accessorKey: "registration_date",
      header: "Reg. Date",
      cell: ({ row }) =>
        row.original.registration_date
          ? new Date(row.original.registration_date).toLocaleDateString()
          : "",
    },
    {
      accessorKey: "expiry_date",
      header: "Expiry Date",
      cell: ({ row }) =>
        row.original.expiry_date
          ? new Date(row.original.expiry_date).toLocaleDateString()
          : "",
    },
    {
      accessorKey: "odometer_reading",
      header: "Odometer",
    },
    {
      accessorKey: "purpose",
      header: "Purpose",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <span
            className={`badge bg-${
              status === "Active"
                ? "success"
                : status === "Expired"
                ? "danger"
                : "secondary"
            }`}
          >
            {status}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Link
            to={`/updateRegistration/${row.original.id}`}
            className="btn btn-sm btn-primary"
          >
            <i className="ri-file-edit-line"></i>
          </Link>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => handleDeleteRegistration(row.original.id)}
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <div className="row">
          <div className="col-12">
            <Breadcrumbs
              title="Vehicle Registrations"
              breadcrumbItems={breadcrumbItems}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <Card className="shadow-sm border-0">
              <CardBody>
                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-2 mb-3">
                  <div>
                    <h5 className="mb-1">Vehicle Registrations Report</h5>
                    <div className="text-muted small">
                      Filter by date range and print the current view.
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2 ms-lg-auto">
                    <Link
                      to="/vehicleRegistrations"
                      className="text-decoration-none"
                    >
                      <Button
                        color="primary"
                        className="d-flex align-items-center gap-1"
                      >
                        <i className="ri-add-line"></i>
                        Register Vehicle
                      </Button>
                    </Link>
                  </div>
                </div>

                <div className="border rounded-4 p-3 p-lg-4 mb-3 bg-light-subtle">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
                    <div>
                      <h6 className="mb-1">Filters</h6>
                      <div className="text-muted small">
                        Narrow the report by registration date range.
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2">
                        Total: {totalRecords}
                      </span>
                      <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-2">
                        Showing: {visibleRecords}
                      </span>
                    </div>
                  </div>

                  <Row className="g-3 align-items-end">
                    <Col md="4" lg="4">
                      <Label className="form-label fw-semibold mb-1">
                        From Date
                      </Label>
                      <Input
                        type="date"
                        name="from_date"
                        value={filters.from_date}
                        onChange={handleFilterChange}
                      />
                    </Col>
                    <Col md="4" lg="4">
                      <Label className="form-label fw-semibold mb-1">
                        To Date
                      </Label>
                      <Input
                        type="date"
                        name="to_date"
                        value={filters.to_date}
                        onChange={handleFilterChange}
                      />
                    </Col>
                    <Col md="4" lg="4">
                      <Row className="g-2">
                        <Col xs="4">
                          <Button
                            color="primary"
                            className="w-100"
                            onClick={applyFilters}
                          >
                            <i className="ri-filter-3-line me-1"></i>
                            Filter
                          </Button>
                        </Col>
                        <Col xs="4">
                          <Button
                            color="light"
                            className="border w-100"
                            onClick={resetFilters}
                          >
                            <i className="ri-refresh-line me-1"></i>
                            Reset
                          </Button>
                        </Col>
                        <Col xs="4">
                          <Button
                            color="success"
                            className="w-100"
                            onClick={handlePrint}
                          >
                            <i className="ri-printer-line me-1"></i>
                            Print
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mt-3 pt-3 border-top">
                    <div className="text-muted small">
                      Active range:{" "}
                      <strong>
                        {filters.from_date || "Any"} → {filters.to_date || "Any"}
                      </strong>
                    </div>
                    <div className="text-muted small">
                      Date filters are applied to the registrations report table.
                    </div>
                  </div>
                </div>

                <TanstackTable data={filteredData} columns={columns} />
              </CardBody>
            </Card>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default RegistrationsList;
